import React, { FC, useState, useCallback, useMemo, useEffect, useRef } from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiTextArea, EuiPanel, EuiTitle, EuiSpacer, EuiButtonGroup, EuiToolTip, EuiButtonIcon, EuiResizableContainer, EuiText, EuiSelect } from '@elastic/eui'

import MarkdownRenderer from './MarkdownRenderer'
import { useScrollSync } from './useScrollSync'

export interface CustomMarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  height?: string | number
  placeholder?: string
  readOnly?: boolean
  hideToolbar?: boolean
  showLineNumbers?: boolean
  enableScrollSync?: boolean
  className?: string
  previewRenderer?: React.FC<{ content: string; className?: string }>
}

type ViewMode = 'edit' | 'preview' | 'split'

interface FormatRange {
  type: 'bold' | 'italic' | 'strikethrough'
  start: number
  end: number
  markerStart: number
  markerEnd: number
  marker: string
}

const CustomMarkdownEditor: FC<CustomMarkdownEditorProps> = ({ value, onChange, height = '400px', placeholder = 'Start writing in Markdown...', readOnly = false, hideToolbar = false, showLineNumbers = false, enableScrollSync = true, className, previewRenderer: PreviewRenderer }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('split')
  const [selectedHeading, setSelectedHeading] = useState<string>('')
  const [activeFormats, setActiveFormats] = useState<{
    bold: boolean
    italic: boolean
    strikethrough: boolean
  }>({
    bold: false,
    italic: false,
    strikethrough: false
  })
  const { leftRef, rightRef } = useScrollSync({
    enabled: enableScrollSync && viewMode === 'split'
  })

  const toolbarRef = useRef<HTMLDivElement>(null)

  const viewModeOptions = useMemo(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        iconType: 'pencil' as const
      },
      {
        id: 'split',
        label: 'Split',
        iconType: 'tableDensityNormal' as const
      },
      {
        id: 'preview',
        label: 'Preview',
        iconType: 'eye' as const
      }
    ],
    []
  )

  const headingOptions = useMemo(
    () => [
      { value: '', text: 'No Heading' },
      { value: '# ', text: 'Heading 1' },
      { value: '## ', text: 'Heading 2' },
      { value: '### ', text: 'Heading 3' },
      { value: '#### ', text: 'Heading 4' },
      { value: '##### ', text: 'Heading 5' },
      { value: '###### ', text: 'Heading 6' }
    ],
    []
  )

  const parseMarkdownFormatting = useCallback((text: string, cursorPos: number): FormatRange[] => {
    const ranges: FormatRange[] = []
    const stack: Array<{ type: 'bold' | 'italic' | 'strikethrough'; start: number; marker: string }> = []

    let i = 0
    while (i < text.length) {
      if (text[i] === '*') {
        if (i + 1 < text.length && text[i + 1] === '*') {
          const marker = '**'
          const existing = stack.findIndex(item => item.type === 'bold' && item.marker === marker)

          if (existing !== -1) {
            const start = stack[existing].start
            ranges.push({
              type: 'bold',
              start: start + 2,
              end: i,
              markerStart: start,
              markerEnd: i + 2,
              marker
            })
            stack.splice(existing, 1)
          } else {
            stack.push({ type: 'bold', start: i, marker })
          }
          i += 2
        } else {
          const marker = '*'
          const existing = stack.findIndex(item => item.type === 'italic' && item.marker === marker)

          if (existing !== -1) {
            const start = stack[existing].start
            ranges.push({
              type: 'italic',
              start: start + 1,
              end: i,
              markerStart: start,
              markerEnd: i + 1,
              marker
            })
            stack.splice(existing, 1)
          } else {
            stack.push({ type: 'italic', start: i, marker })
          }
          i += 1
        }
      } else if (text[i] === '~' && i + 1 < text.length && text[i + 1] === '~') {
        const marker = '~~'
        const existing = stack.findIndex(item => item.type === 'strikethrough' && item.marker === marker)

        if (existing !== -1) {
          const start = stack[existing].start
          ranges.push({
            type: 'strikethrough',
            start: start + 2,
            end: i,
            markerStart: start,
            markerEnd: i + 2,
            marker
          })
          stack.splice(existing, 1)
        } else {
          stack.push({ type: 'strikethrough', start: i, marker })
        }
        i += 2
      } else {
        i += 1
      }
    }

    return ranges
  }, [])

  const detectCurrentFormatting = useCallback(
    (textarea: HTMLTextAreaElement) => {
      const cursorPosition = textarea.selectionStart
      const textValue = textarea.value

      const lineStart = textValue.lastIndexOf('\n', cursorPosition - 1) + 1
      const lineEnd = textValue.indexOf('\n', cursorPosition)
      const lineText = textValue.substring(lineStart, lineEnd === -1 ? textValue.length : lineEnd)
      const cursorInLine = cursorPosition - lineStart

      const allRanges = parseMarkdownFormatting(lineText, cursorInLine)

      const activeRanges = allRanges.filter(range => {
        if (cursorInLine >= range.markerStart && cursorInLine <= range.markerEnd) {
          return true
        }

        const overlappingRange = allRanges.find(otherRange => otherRange !== range && cursorInLine >= otherRange.markerStart && cursorInLine <= otherRange.markerEnd && (Math.abs(range.markerStart - otherRange.markerStart) <= 2 || Math.abs(range.markerEnd - otherRange.markerEnd) <= 2))

        if (overlappingRange) {
          return true
        }

        const containingRange = allRanges.find(otherRange => otherRange !== range && cursorInLine >= otherRange.markerStart && cursorInLine <= otherRange.markerEnd && range.markerStart >= otherRange.markerStart && range.markerEnd <= otherRange.markerEnd)

        return !!containingRange
      })

      setActiveFormats({
        bold: activeRanges.some(range => range.type === 'bold'),
        italic: activeRanges.some(range => range.type === 'italic'),
        strikethrough: activeRanges.some(range => range.type === 'strikethrough')
      })
    },
    [parseMarkdownFormatting]
  )

  const removeFormatting = useCallback(
    (textarea: HTMLTextAreaElement, formatType: 'bold' | 'italic' | 'strikethrough') => {
      const cursorPosition = textarea.selectionStart
      const textValue = textarea.value

      const lineStart = textValue.lastIndexOf('\n', cursorPosition - 1) + 1
      const lineEnd = textValue.indexOf('\n', cursorPosition)
      const lineText = textValue.substring(lineStart, lineEnd === -1 ? textValue.length : lineEnd)
      const cursorInLine = cursorPosition - lineStart

      const allRanges = parseMarkdownFormatting(lineText, cursorInLine)

      const candidateRanges = allRanges.filter(range => {
        if (range.type !== formatType) return false

        if (cursorInLine >= range.markerStart && cursorInLine <= range.markerEnd) {
          return true
        }

        const overlappingRange = allRanges.find(otherRange => otherRange !== range && cursorInLine >= otherRange.markerStart && cursorInLine <= otherRange.markerEnd && (Math.abs(range.markerStart - otherRange.markerStart) <= 2 || Math.abs(range.markerEnd - otherRange.markerEnd) <= 2))

        if (overlappingRange) {
          return true
        }

        const containingRange = allRanges.find(otherRange => otherRange !== range && cursorInLine >= otherRange.markerStart && cursorInLine <= otherRange.markerEnd && range.markerStart >= otherRange.markerStart && range.markerEnd <= otherRange.markerEnd)

        return !!containingRange
      })

      const targetRange = candidateRanges.reduce((innermost, current) => {
        if (!innermost) return current
        const innermostSize = innermost.markerEnd - innermost.markerStart
        const currentSize = current.markerEnd - current.markerStart
        return currentSize < innermostSize ? current : innermost
      }, null as FormatRange | null)

      if (!targetRange) return false

      const globalMarkerStart = lineStart + targetRange.markerStart
      const globalMarkerEnd = lineStart + targetRange.markerEnd

      const beforeMarker = textValue.substring(0, globalMarkerStart)
      const content = textValue.substring(globalMarkerStart + targetRange.marker.length, globalMarkerEnd - targetRange.marker.length)
      const afterMarker = textValue.substring(globalMarkerEnd)

      const newValue = beforeMarker + content + afterMarker
      const markerLength = targetRange.marker.length

      let newCursorPos: number
      if (cursorPosition <= globalMarkerStart + markerLength) {
        newCursorPos = globalMarkerStart
      } else if (cursorPosition >= globalMarkerEnd - markerLength) {
        newCursorPos = globalMarkerEnd - markerLength * 2
      } else {
        newCursorPos = cursorPosition - markerLength
      }

      onChange(newValue)

      setTimeout(() => {
        textarea.setSelectionRange(newCursorPos, newCursorPos)
        textarea.focus()
        detectCurrentFormatting(textarea)
      }, 0)

      return true
    },
    [parseMarkdownFormatting, onChange, detectCurrentFormatting]
  )

  const handleInsertText = useCallback(
    (text: string, cursorOffset = 0) => {
      const textarea = document.querySelector('.custom-markdown-editor textarea') as HTMLTextAreaElement
      if (!textarea) return

      const formatType = text === '**' ? 'bold' : text === '*' ? 'italic' : text === '~~' ? 'strikethrough' : null

      if (formatType) {
        const currentFormats = {
          bold: activeFormats.bold,
          italic: activeFormats.italic,
          strikethrough: activeFormats.strikethrough
        }

        if (currentFormats[formatType]) {
          if (removeFormatting(textarea, formatType)) {
            return
          }
        }
      }

      let start = textarea.selectionStart
      let end = textarea.selectionEnd
      let selectedText = textarea.value.substring(start, end)

      const isInlineFormatting = text === '**' || text === '*' || text === '~~'

      if (isInlineFormatting && start === end) {
        const wordBoundaryRegex = /[\s\n\r\t.,;:!?()[\]{}'"]/
        let wordStart = start
        let wordEnd = end

        while (wordStart > 0 && !wordBoundaryRegex.test(textarea.value[wordStart - 1])) {
          wordStart--
        }

        while (wordEnd < textarea.value.length && !wordBoundaryRegex.test(textarea.value[wordEnd])) {
          wordEnd++
        }

        if (wordStart < wordEnd && wordStart !== start) {
          start = wordStart
          end = wordEnd
          selectedText = textarea.value.substring(start, end)
        }
      }

      let newText: string
      let insertPosition = start
      let newCursorPos = start + text.length + cursorOffset
      let deleteLength = end - start

      if (isInlineFormatting) {
        newText = text + selectedText + text
        newCursorPos = start + text.length + selectedText.length + text.length
      } else if (text.includes('{}')) {
        newText = text.replace('{}', selectedText)
      } else if (text.startsWith('#')) {
        const lineStart = textarea.value.lastIndexOf('\n', start - 1) + 1
        const lineEnd = textarea.value.indexOf('\n', start)
        const currentLine = textarea.value.substring(lineStart, lineEnd === -1 ? textarea.value.length : lineEnd)

        const existingHeadingMatch = currentLine.match(/^(#+)\s*/)
        if (existingHeadingMatch) {
          const existingLevel = existingHeadingMatch[1]
          const newLevel = text.trim()
          if (existingLevel === newLevel) {
            newText = currentLine.substring(existingHeadingMatch[0].length)
            newCursorPos = lineStart
          } else {
            newText = text + currentLine.substring(existingHeadingMatch[0].length)
            newCursorPos = lineStart + text.length
          }
        } else {
          newText = text + currentLine
          newCursorPos = lineStart + text.length
        }

        insertPosition = lineStart
        deleteLength = lineEnd === -1 ? textarea.value.length - lineStart : lineEnd - lineStart
      } else if (text.match(/^(- |1\. |> |\| )/)) {
        const lineStart = textarea.value.lastIndexOf('\n', start - 1) + 1
        const lineEnd = textarea.value.indexOf('\n', start)
        const currentLine = textarea.value.substring(lineStart, lineEnd === -1 ? textarea.value.length : lineEnd)

        if (currentLine.trim() !== '' && start !== lineStart) {
          newText = '\n\n' + text
          insertPosition = start
          newCursorPos = start + newText.length
          deleteLength = 0
        } else {
          insertPosition = lineStart
          newText = text
          newCursorPos = lineStart + text.length
          deleteLength = 0
        }
      } else if (text.startsWith('[') && text.includes('](')) {
        const lineStart = textarea.value.lastIndexOf('\n', start - 1) + 1
        const beforeCursor = textarea.value.substring(lineStart, start)

        if (beforeCursor.trim() !== '' && !beforeCursor.endsWith(' ')) {
          newText = ' ' + text
        } else {
          newText = text
        }
      } else {
        newText = text + selectedText + text
      }

      textarea.focus()
      textarea.setSelectionRange(insertPosition, insertPosition + deleteLength)

      try {
        document.execCommand('insertText', false, newText)
      } catch (error) {
        textarea.setRangeText(newText, insertPosition, insertPosition + deleteLength, 'end')
        const newValue = textarea.value
        onChange(newValue)
      }

      setTimeout(() => {
        textarea.setSelectionRange(newCursorPos, newCursorPos)
        detectCurrentFormatting(textarea)
      }, 0)
    },
    [value, onChange, activeFormats, removeFormatting, detectCurrentFormatting]
  )

  const detectCurrentHeading = useCallback((textarea: HTMLTextAreaElement) => {
    const cursorPosition = textarea.selectionStart
    const textValue = textarea.value

    const lineStart = textValue.lastIndexOf('\n', cursorPosition - 1) + 1
    const lineEnd = textValue.indexOf('\n', cursorPosition)
    const currentLine = textValue.substring(lineStart, lineEnd === -1 ? textValue.length : lineEnd)

    const headingMatch = currentLine.match(/^(#{1,6})\s/)
    if (headingMatch) {
      const headingLevel = headingMatch[1] + ' '
      setSelectedHeading(headingLevel)
    } else {
      setSelectedHeading('')
    }
  }, [])

  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value)
      detectCurrentHeading(e.target)
      detectCurrentFormatting(e.target)
    },
    [onChange, detectCurrentHeading, detectCurrentFormatting]
  )

  const handleTextareaSelect = useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      detectCurrentHeading(e.currentTarget)
      detectCurrentFormatting(e.currentTarget)
    },
    [detectCurrentHeading, detectCurrentFormatting]
  )

  const handleTextareaClick = useCallback(
    (e: React.MouseEvent<HTMLTextAreaElement>) => {
      detectCurrentHeading(e.currentTarget)
      detectCurrentFormatting(e.currentTarget)
    },
    [detectCurrentHeading, detectCurrentFormatting]
  )

  const handleHeadingChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const headingLevel = e.target.value
      if (headingLevel) {
        handleInsertText(headingLevel, 0)
        setSelectedHeading('')
      } else {
        const textarea = document.querySelector('.custom-markdown-editor textarea') as HTMLTextAreaElement
        if (!textarea) return

        const start = textarea.selectionStart
        const textValue = textarea.value

        const lineStart = textValue.lastIndexOf('\n', start - 1) + 1
        const lineEnd = textValue.indexOf('\n', start)
        const currentLine = textValue.substring(lineStart, lineEnd === -1 ? textValue.length : lineEnd)

        const headingMatch = currentLine.match(/^(#{1,6})\s*/)
        if (headingMatch) {
          const newText = currentLine.substring(headingMatch[0].length)
          const deleteLength = lineEnd === -1 ? textValue.length - lineStart : lineEnd - lineStart

          textarea.focus()
          textarea.setSelectionRange(lineStart, lineStart + deleteLength)

          try {
            document.execCommand('insertText', false, newText)
          } catch (error) {
            textarea.setRangeText(newText, lineStart, lineStart + deleteLength, 'end')
            const newValue = textarea.value
            onChange(newValue)
          }

          setTimeout(() => {
            textarea.setSelectionRange(lineStart, lineStart)
            detectCurrentHeading(textarea)
          }, 0)
        }
      }
    },
    [handleInsertText, onChange, detectCurrentHeading]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault()

        const textarea = e.currentTarget
        const cursorPosition = textarea.selectionStart
        const textValue = textarea.value

        if (e.key === 'Home') {
          const lineStart = textValue.lastIndexOf('\n', cursorPosition - 1) + 1
          const newPosition = e.ctrlKey || e.metaKey ? 0 : lineStart
          textarea.setSelectionRange(newPosition, newPosition)
        } else if (e.key === 'End') {
          const lineEnd = textValue.indexOf('\n', cursorPosition)
          const newPosition = e.ctrlKey || e.metaKey ? textValue.length : lineEnd === -1 ? textValue.length : lineEnd
          textarea.setSelectionRange(newPosition, newPosition)
        }

        setTimeout(() => {
          detectCurrentHeading(textarea)
          detectCurrentFormatting(textarea)
        }, 0)
      } else {
        setTimeout(() => {
          detectCurrentHeading(e.currentTarget)
          detectCurrentFormatting(e.currentTarget)
        }, 0)
      }
    },
    [detectCurrentHeading, detectCurrentFormatting]
  )

  useEffect(() => {
    const textarea = document.querySelector('.custom-markdown-editor textarea') as HTMLTextAreaElement
    if (textarea) {
      detectCurrentHeading(textarea)
      detectCurrentFormatting(textarea)
    }
  }, [value, detectCurrentHeading, detectCurrentFormatting])

  const toolbarButtons = useMemo(
    () => [
      { icon: 'editorBold', tooltip: 'Bold', action: () => handleInsertText('**'), isActive: activeFormats.bold },
      { icon: 'editorItalic', tooltip: 'Italic', action: () => handleInsertText('*'), isActive: activeFormats.italic },
      { icon: 'editorStrike', tooltip: 'Strikethrough', action: () => handleInsertText('~~'), isActive: activeFormats.strikethrough },
      { icon: 'separator' },
      { icon: 'headingDropdown' },
      { icon: 'separator' },
      { icon: 'editorUnorderedList', tooltip: 'Bullet List', action: () => handleInsertText('- Bullet point 1\n- Bullet point 2', 0) },
      { icon: 'editorOrderedList', tooltip: 'Numbered List', action: () => handleInsertText('1. Item 1\n2. Item 2', 0) },
      { icon: 'quote', tooltip: 'Quote', action: () => handleInsertText('> Quote text here', 0) },
      { icon: 'separator' },
      { icon: 'editorLink', tooltip: 'Link', action: () => handleInsertText('[Click here for website](https://google.com)') },
      { icon: 'editorTable', tooltip: 'Table', action: () => handleInsertText('| Column 1 | Column 2 |\n|----------|----------|\n| Cell 1 | Cell 2 |') }
    ],
    [handleInsertText, activeFormats.bold, activeFormats.italic, activeFormats.strikethrough]
  )

  const renderToolbar = () => {
    if (hideToolbar || readOnly) return null

    return (
      <div ref={toolbarRef}>
        <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiFlexGroup alignItems='center' gutterSize='xs' responsive={false}>
              {toolbarButtons.map((button, index) =>
                button.icon === 'separator' ? (
                  <EuiFlexItem key={index} grow={false}>
                    <div
                      style={{
                        width: '1px',
                        height: '24px',
                        backgroundColor: '#d3dae6',
                        margin: '0 4px'
                      }}
                    />
                  </EuiFlexItem>
                ) : button.icon === 'headingDropdown' ? (
                  <EuiFlexItem key={index} grow={false}>
                    <EuiSelect
                      options={headingOptions}
                      value={selectedHeading}
                      onChange={handleHeadingChange}
                      style={{
                        minWidth: '120px',
                        fontSize: '14px'
                      }}
                    />
                  </EuiFlexItem>
                ) : (
                  <EuiFlexItem key={index} grow={false}>
                    <EuiToolTip content={button.tooltip}>
                      <EuiButtonIcon iconType={button.icon} onClick={button.action} size='s' aria-label={button.tooltip} color={button.isActive ? 'primary' : 'text'} display={button.isActive ? 'fill' : 'empty'} />
                    </EuiToolTip>
                  </EuiFlexItem>
                )
              )}
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size='s' />
      </div>
    )
  }

  const editorStyle = useMemo(
    () => ({
      height: '100%',
      resize: 'none' as const,
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace',
      fontSize: '14px',
      lineHeight: '1.5',
      border: '1px solid #d3dae6',
      borderRadius: '6px',
      padding: '12px',
      overflow: 'auto' as const
    }),
    []
  )

  const previewContainerStyle = useMemo(
    () => ({
      height: '100%',
      overflow: 'auto' as const,
      border: '1px solid #d3dae6',
      borderRadius: '6px',
      padding: '12px',
      backgroundColor: '#fafbfd'
    }),
    []
  )

  const renderEditor = (ref?: React.Ref<HTMLTextAreaElement>) => (
    <div className='custom-markdown-editor' style={{ height: '100%' }}>
      <EuiTextArea inputRef={ref} value={value} onChange={handleTextareaChange} onKeyDown={handleKeyDown} onSelect={handleTextareaSelect} onClick={handleTextareaClick} placeholder={placeholder} readOnly={readOnly} style={editorStyle} fullWidth resize='none' />
    </div>
  )

  const renderPreview = (ref?: React.Ref<HTMLDivElement>) => (
    <div ref={ref} style={previewContainerStyle}>
      {value.trim() ? (
        PreviewRenderer ? (
          <PreviewRenderer content={value} />
        ) : (
          <MarkdownRenderer content={value} />
        )
      ) : (
        <EuiText color='subdued' textAlign='center' style={{ marginBottom: '50%' }}>
          <p>Enter new content on the left. A preview version will appear here.</p>
        </EuiText>
      )}
    </div>
  )

  const renderContent = () => {
    const containerHeight = typeof height === 'number' ? `${height}px` : `calc(${height} - 90px - ${toolbarRef.current?.offsetHeight}px)`

    switch (viewMode) {
      case 'edit':
        return renderEditor(leftRef as any)

      case 'preview':
        return renderPreview(rightRef as any)

      case 'split':
      default:
        return (
          <EuiResizableContainer style={{ height: containerHeight }}>
            {(EuiResizablePanel, EuiResizableButton) => (
              <>
                <EuiResizablePanel initialSize={50} minSize='30%'>
                  <div style={{ height: '100%', paddingBottom: '2rem' }}>{renderEditor(leftRef as any)}</div>
                </EuiResizablePanel>

                <EuiResizableButton />

                <EuiResizablePanel initialSize={50} minSize='30%'>
                  <div style={{ height: '100%', paddingBottom: '2rem' }}>{renderPreview(rightRef as any)}</div>
                </EuiResizablePanel>
              </>
            )}
          </EuiResizableContainer>
        )
    }
  }

  return (
    <EuiPanel paddingSize='m' className={className} style={{ height: `calc(${height} - 90px)` }}>
      {renderToolbar()}
      {renderContent()}
    </EuiPanel>
  )
}

export default CustomMarkdownEditor
